import React from "react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { BUSINESS } from "@/constants/business";

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service | Vila Elevators & Escalators"
        description="Terms and conditions governing the use of vilaelevators.in and our services."
        path="/terms"
      />

      <PageHeader
        eyebrow="Legal"
        title="Terms of Service"
        desc={`Last updated: ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`}
      />

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-8">
          <Block title="1. Acceptance of Terms">
            <P>
              These Terms of Service ("Terms") govern your access to and use of the website{" "}
              <a href="https://vilaelevators.in" className="text-[#b08d57] underline">vilaelevators.in</a>{" "}
              (the "Service") operated by Vila Elevators &amp; Escalators ("Company", "we", "us"). By accessing or using the Service, you agree to be bound by these Terms.
            </P>
          </Block>

          <Block title="2. About Vila Elevators &amp; Escalators">
            <P>
              We are a Bengaluru-based manufacturer and service provider of elevators, escalators, home lifts, freight elevators, hospital lifts and moving walkways, serving Karnataka, Andhra Pradesh, Telangana and Tamil Nadu.
            </P>
          </Block>

          <Block title="3. Enquiries, Quotes and Site Surveys">
            <P>
              Information on this website, including cost-calculator estimates, is <strong className="text-[#1a1a1a]">indicative only</strong>. All quotations, warranties, delivery timelines and technical specifications are confirmed only after a formal site survey and written proposal issued by our authorised representative.
            </P>
            <P>
              A formal contract between you and Vila Elevators &amp; Escalators is created only upon signature of a written purchase order or service agreement issued by our office.
            </P>
          </Block>

          <Block title="4. Use of the Website">
            <P>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</P>
            <List
              items={[
                "Use the Service in any way that violates applicable Indian or state laws",
                "Attempt to reverse-engineer, copy or resell any part of the Service",
                "Introduce viruses, trojans, worms, or other harmful material",
                "Attempt to gain unauthorised access to our servers or infrastructure",
                "Impersonate or misrepresent your affiliation with any person or entity",
              ]}
            />
          </Block>

          <Block title="5. Intellectual Property">
            <P>
              All content on this website — including logos, product descriptions, images, blog articles, cost calculator methodology, and design — is the intellectual property of Vila Elevators &amp; Escalators. It is protected by Indian and international copyright laws. You may not reproduce, redistribute or commercially exploit any content without written permission.
            </P>
          </Block>

          <Block title="6. Products &amp; Services">
            <P>
              All product specifications, images and descriptions on this website are for informational purposes only. Final product specifications may vary based on site conditions, customer requirements and applicable safety regulations. We reserve the right to modify specifications without prior notice.
            </P>
          </Block>

          <Block title="7. Pricing &amp; Payment">
            <P>
              Estimated price ranges shown on the website or the elevator cost calculator are non-binding and for indicative purposes only. Actual pricing depends on site survey, cabin customisation, drive type, statutory approvals, transport, and installation conditions. All commercial terms (deposit, milestones, warranty, delivery timelines) are governed by the formal purchase order agreed between the parties.
            </P>
          </Block>

          <Block title="8. Warranties">
            <P>
              Product warranties (where applicable) are as stated in the formal purchase order or product warranty document. No warranty is created by this website or any marketing material. Our AMC (Annual Maintenance Contract) terms are governed by a separate AMC agreement.
            </P>
          </Block>

          <Block title="9. Limitation of Liability">
            <P>
              To the maximum extent permitted by applicable law, Vila Elevators &amp; Escalators shall not be liable for any indirect, incidental, special, consequential or punitive damages arising out of or in connection with your use of the website — including, without limitation, loss of profits, data, or business opportunities. Our aggregate liability for direct damages, if any, shall not exceed the amount actually paid by you to us under a specific contract giving rise to the claim.
            </P>
          </Block>

          <Block title="10. Third-Party Links">
            <P>
              Our website may contain links to third-party websites (e.g., social media platforms). We are not responsible for the content, privacy policies or practices of any third-party sites.
            </P>
          </Block>

          <Block title="11. Privacy">
            <P>
              Your use of the Service is also governed by our{" "}
              <a href="/privacy-policy" className="text-[#b08d57] underline">Privacy Policy</a>, which describes how we collect and process your personal data.
            </P>
          </Block>

          <Block title="12. Governing Law &amp; Jurisdiction">
            <P>
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.
            </P>
          </Block>

          <Block title="13. Changes to Terms">
            <P>
              We reserve the right to modify these Terms at any time. Continued use of the Service after changes are posted constitutes acceptance of the modified Terms.
            </P>
          </Block>

          <Block title="14. Contact Us">
            <P>Questions about these Terms? Reach us at:</P>
            <div className="border border-[#e5ddd0] bg-[#faf7f2]/40 p-6 mt-4">
              <div className="text-[#1a1a1a] font-display text-lg mb-2">{BUSINESS.name}</div>
              <div className="text-[#6b6b6b] text-sm space-y-1">
                <div>{BUSINESS.address.line1}, {BUSINESS.address.line2}</div>
                <div>{BUSINESS.address.city} – {BUSINESS.address.postal}, {BUSINESS.address.state}</div>
                <div className="pt-2">
                  Email: <a href={`mailto:${BUSINESS.email}`} className="text-[#b08d57]">{BUSINESS.email}</a>
                </div>
                <div>
                  Phone: <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#b08d57]">{BUSINESS.phone}</a>
                </div>
              </div>
            </div>
          </Block>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#1a1a1a] mb-4">{title}</h2>
      <div className="space-y-3 text-[#4a4a4a] leading-relaxed">{children}</div>
    </div>
  );
}
function P({ children }) {
  return <p className="text-[#4a4a4a] leading-relaxed">{children}</p>;
}
function List({ items }) {
  return (
    <ul className="space-y-2 text-[#4a4a4a]">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="w-1.5 h-1.5 bg-[#b08d57] mt-2 flex-shrink-0" /> <span>{i}</span>
        </li>
      ))}
    </ul>
  );
}
